import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { orderKeys } from './use-sales-order-query'
import { useSseQueryRecovery } from './use-sse-query-recovery'
import { workActivityKeys } from './use-work-activities-query'
import type { WorkActivity } from '@/lib/api/work-activity-api'
import type { z } from 'zod'
import type {
    StationNewOrderEvent,
    StationNotificationEvent as WireStationNotificationEvent,
} from '@/lib/schemas/station-event-schemas'
import type { SseValidationErrorHandler } from '@/lib/schemas/sse-event-schemas'
import type { SseEventName } from '@/lib/sse-stream-registry'
import type { StationAudioContext } from '@/lib/station-audio-telemetry'
import { createStationAudioContext } from '@/lib/station-audio-telemetry'
import { SSE_URL } from '@/lib/config'
import { createStationEventSchemas } from '@/lib/schemas/station-event-schemas'
import { createSseListener } from '@/lib/schemas/sse-event-schemas'
import { subscribeSse } from '@/lib/sse-stream-registry'

export type { StationNewOrderEvent } from '@/lib/schemas/station-event-schemas'

/** Biến động bên bảng công việc — máy trạm dùng để nhảy màn ngay. */
export interface StationWorkActivityEvent {
    work_activity_id: string
    work_activity_type?: string
    title?: string
    event?: string
    occurred_at?: string
    /** Giờ của lần diễn ra sự kiện (chỉ hai mốc nhắc sự kiện mới có). */
    starts_at?: string
}

export type StationNotificationEvent = WireStationNotificationEvent & {
    /** Browser-only observer added after the bank-in wire payload is validated. */
    audio_context?: StationAudioContext
}

const stationQueryKeys = [orderKeys.lists()]

/** Validated station events feed the order board and its independent audio callbacks. */
export function useStationSSE(
    onNewOrder?: (event: StationNewOrderEvent) => void,
    onNotification?: (event: StationNotificationEvent) => void,
    // Only the production station opts into machine announcements.
    onMachineFinishing?: (event: StationNotificationEvent) => void,
    onValidationError?: SseValidationErrorHandler,
    /**
     * Biến động bên bảng công việc (giao việc mới, sắp tới giờ họp, việc quá
     * hạn). Chỉ máy trạm đơn hàng truyền hàm này để nhảy sang màn công việc.
     */
    onWorkActivity?: (event: StationWorkActivityEvent) => void,
    /**
     * Sổ xưởng vừa có mẻ mới (#M-16). Handler RIÊNG chứ không dùng chung
     * `onNotification`: màn đơn hàng và màn sản xuất đọc hai nhóm case khác
     * nhau, gộp vào một cửa là loa xưởng đọc cả tiền về ngân hàng.
     */
    onLedgerEntry?: (event: StationNotificationEvent) => void,
) {
    const queryClient = useQueryClient()
    const [connected, setConnected] = useState(false)
    const { dataStatus, refetch } = useSseQueryRecovery(
        stationQueryKeys,
        15_000,
    )
    const handlersRef = useRef({
        onNewOrder,
        onNotification,
        onMachineFinishing,
        onLedgerEntry,
        onValidationError,
        onWorkActivity,
    })
    handlersRef.current = {
        onNewOrder,
        onNotification,
        onMachineFinishing,
        onLedgerEntry,
        onValidationError,
        onWorkActivity,
    }

    useEffect(() => {
        const schemas = createStationEventSchemas()
        const listen = <TSchema extends z.ZodType>(
            event: SseEventName,
            schema: TSchema,
            handler: (data: z.output<TSchema>) => void,
        ) =>
            createSseListener(event, schema, handler, (failure) =>
                handlersRef.current.onValidationError?.(failure),
            )
        const refreshOrders = () => {
            void queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
        }
        type DeliveryName =
            | 'order_start_delivery'
            | 'order_delivery_success'
            | 'order_stock_out_done'
            | 'order_delivery_failed'
            | 'order_payment_received'
        const delivery = (event: DeliveryName, type: string) =>
            listen(event, schemas[event], (data) => {
                refreshOrders()
                handlersRef.current.onNotification?.({
                    type,
                    speech_template: data.speech_template,
                    order_id: data.order_id,
                    occurred_at: data.occurred_at,
                    payload: {
                        order_code: data.order_code,
                        internal_order_code: data.internal_order_code,
                        customer_name: data.customer_name,
                        customer_phone: data.customer_phone,
                        amount: data.amount,
                        created_by_name: data.created_by_name,
                        driver_assignee_name: data.driver_assignee_name,
                        stock_out_created_by_name:
                            data.stock_out_created_by_name,
                        stock_out_approved_by_name:
                            data.stock_out_approved_by_name,
                    },
                })
            })
        return subscribeSse({
            url: SSE_URL,
            onConnectedChange: (isConnected) => {
                setConnected(isConnected)
                if (isConnected) void refetch()
            },
            listeners: {
                station_new_order: listen(
                    'station_new_order',
                    schemas.station_new_order,
                    (data) => {
                        refreshOrders()
                        handlersRef.current.onNewOrder?.(data)
                    },
                ),
                notification: listen(
                    'notification',
                    schemas.notification,
                    (data) => handlersRef.current.onNotification?.(data),
                ),
                order_start_delivery: delivery(
                    'order_start_delivery',
                    'in_delivery',
                ),
                order_delivery_success: delivery(
                    'order_delivery_success',
                    'delivered',
                ),
                order_stock_out_done: delivery(
                    'order_stock_out_done',
                    'stock_out',
                ),
                order_delivery_failed: delivery(
                    'order_delivery_failed',
                    'delivery_failed',
                ),
                order_payment_received: delivery(
                    'order_payment_received',
                    'payment_received',
                ),
                // Incoming funds are announced independently of order matching or receipts.
                station_bank_in: listen(
                    'station_bank_in',
                    schemas.station_bank_in,
                    (data) => {
                        const audio_context = createStationAudioContext()
                        audio_context?.report('received')
                        handlersRef.current.onNotification?.({
                            type: 'bank_in',
                            audio_context,
                            speech_template: data.speech_template,
                            occurred_at: data.occurred_at,
                            payload: {
                                amount: data.amount,
                                content: data.content,
                            },
                        })
                    },
                ),
                // Biến động công việc: làm mới bảng máy trạm rồi MỚI báo cho
                // màn biết để nhảy sang, không đợi hết vòng xoay 30 giây.
                //
                // Thứ tự ở đây là load-bearing. Báo ngay lúc nhận sự kiện thì
                // màn đọc cache CŨ, và hỏng cả hai đầu: bảng đang rỗng (việc
                // đầu ca) thì cú nhảy bị nuốt vì "chưa có gì để xem", còn hoạt
                // động nằm ngoài trần của bảng thì màn nhảy sang một bảng không
                // hề đổi. Đợi nạp xong rồi kiểm tra đúng id là điều kiện thật:
                // chỉ nhảy khi có thứ để nhìn.
                station_work_activity: listen(
                    'station_work_activity',
                    schemas.station_work_activity,
                    (data) => {
                        void (async () => {
                            await queryClient.invalidateQueries({
                                queryKey: workActivityKeys.station(),
                            })
                            const board = queryClient.getQueryData<
                                WorkActivity[]
                            >(workActivityKeys.station())
                            if (
                                board?.some(
                                    (a) => a.id === data.work_activity_id,
                                )
                            ) {
                                handlersRef.current.onWorkActivity?.(data)
                            }
                        })()
                    },
                ),
                station_machine_finishing: listen(
                    'station_machine_finishing',
                    schemas.station_machine_finishing,
                    (data) => {
                        handlersRef.current.onMachineFinishing?.({
                            type: 'machine_finishing',
                            speech_template: data.speech_template,
                            payload: {
                                machine_code: data.machine_code,
                                remaining_minutes: data.remaining_minutes,
                            },
                        })
                    },
                ),
                station_packaging_log: listen(
                    'station_packaging_log',
                    schemas.station_packaging_log,
                    (data) => {
                        handlersRef.current.onLedgerEntry?.({
                            type: 'packaging_log_entry',
                            speech_template: data.speech_template,
                            payload: {
                                ledger_name: data.ledger_name,
                                writer_name: data.writer_name,
                                ledger: data.ledger,
                                entry_id: data.entry_id,
                            },
                        })
                    },
                ),
                station_stock_low: listen(
                    'station_stock_low',
                    schemas.station_stock_low,
                    (data) => {
                        handlersRef.current.onNotification?.({
                            type: 'stock_low',
                            speech_template: data.speech_template,
                            occurred_at: data.occurred_at,
                            payload: {
                                product_name: data.product_name,
                                quantity_remaining: data.quantity_remaining,
                                unit: data.unit,
                            },
                        })
                    },
                ),
                station_lot_debt: listen(
                    'station_lot_debt',
                    schemas.station_lot_debt,
                    (data) => {
                        handlersRef.current.onNotification?.({
                            type: 'lot_debt',
                            speech_template: data.speech_template,
                            occurred_at: data.occurred_at,
                            payload: {
                                internal_order_code: data.ma_don_noi_bo,
                                product_name: data.ten_san_pham,
                                so_luong: data.so_luong,
                                unit: data.don_vi,
                            },
                        })
                    },
                ),
            },
        })
    }, [queryClient, refetch])
    return { connected, dataStatus, refetch }
}
